import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { Department } from "@/types";
import { toast } from "sonner";
import { UserRole } from "@/types/enums";

export function useDepartmentSettings() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const companyId = user?.companyId || null;

  // Fetch departments based on user role and company
  const fetchDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('Unknown error');

      if (!user) {
        throw new Error("User is not authenticated");
      }

      // Get departments
      const { data: departmentData, error: departmentsError } = await supabase
        .from('departments')
        .select('*')
        .eq('is_del', 'N');
      
      if (departmentsError) {
        throw departmentsError;
      }
      
      // Map snake_case DB fields to our Department type
      const mappedDepartments = (departmentData || []).map((dept: any) => ({
        id: dept.id,
        name: dept.name,
        description: dept.description || null,
        company_id: dept.company_id
      }));
      setDepartments(mappedDepartments);
      setIsLoading(false);
    } catch {

      setError("Failed to fetch departments");
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDepartments();
  }, [user, fetchDepartments]); // fetchDepartments is now stable via useCallback

  const addDepartment = async (newDepartment: Omit<Department, 'id'>) => {
    try {
      if (!user) {
        toast.error('You must be logged in to add a department');
        return false;
      }
      
      if (!companyId && !newDepartment.company_id) {
        toast.error('Company ID is required to add a department');
        return false;
      }
      
      const { data, error } = await supabase
        .from('departments')
        .insert({
          name: newDepartment.name,
          description: newDepartment.description || null,
          company_id: companyId || newDepartment.company_id
        } as any)
        .select();
      
      if (error) {
        toast.error(`Failed to add department: ${error.message}`);
        return false;
      }
      
      if (!data || data.length === 0) {
        toast.error('Failed to add department: No data returned');
        return false;
      }
      
      // Convert from DB format to our type format
      const newDept: Department = {
        id: (data as any)[0].id,
        name: (data as any)[0].name,
        description: (data as any)[0].description || null,
        company_id: (data as any)[0].company_id,
        companyId: (data as any)[0].company_id,
      };
      
      setDepartments([...departments, newDept]);
      toast.success('Department added successfully');
      return true;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to add department');
      return false;
    }
  };

  const updateDepartment = async (updatedDepartment: Department) => {
    try {
      if (!user) {
        toast.error('You must be logged in to update a department');
        return false;
      }
      
      const { error } = await (supabase as any)
        .from('departments')
        .update({
          name: updatedDepartment.name,
          description: updatedDepartment.description,
          company_id: updatedDepartment.company_id
        })
        .eq('id', updatedDepartment.id);
      
      if (error) {
        throw error;
      }
      
      setDepartments(departments.map(dept => 
        dept.id === updatedDepartment.id ? updatedDepartment : dept
      ));
      toast.success('Department updated successfully');
      return true;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to update department');
      return false;
    }
  };

  const deleteDepartment = async (id: string) => {
    try {
      if (!user) {
        toast.error('You must be logged in to delete a department');
        return false;
      }
      
      // First check if the department exists
      const { data: checkData, error: checkError } = await supabase
        .from('departments')
        .select('id, name')
        .eq('id', id)
        .single();
      
      if (checkError) {
        toast.error('Department not found or cannot be accessed');
        return false;
      }
      
      if (!checkData) {
        toast.error('Department not found');
        return false;
      }
      
      // Proceed with soft deletion
      const { error } = await (supabase as any)
        .from('departments')
        .update({ is_del: 'Y' })
        .eq('id', id);
      
      if (error) {
        toast.error(error.message || 'Failed to delete department');
        return false;
      }
      
      // Update state to remove the deleted department
      setDepartments(prev => prev.filter(dept => dept.id !== id));
      return true;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
      return false;
    }
  };

  return {
    departments,
    isLoading,
    error: null,
    companyId,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    isAppOwner: user?.role === UserRole.APP_OWNER
  };
}
